import { Tag } from './Tag';
import type { FilterState } from './Sidebar';

/**
 * Mobile-only horizontal filter strip. Renders below the page heading and replaces
 * the left sidebar (which is hidden under lg). View tabs left, topic chips right,
 * everything horizontally scrollable on narrow viewports.
 */
export function MobileFilterBar({
  state,
  setState,
  counts,
  tags,
}: {
  state: FilterState;
  setState: (next: FilterState) => void;
  counts: { all: number; upcoming: number; past: number };
  tags: string[];
}) {
  return (
    <div className="lg:hidden -mx-6 mb-6 border-y border-line bg-canvas/80 backdrop-blur">
      <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide px-6 py-2.5">
        <ViewChip
          label="All"
          count={counts.all}
          active={state.view === 'all'}
          onClick={() => setState({ ...state, view: 'all' })}
        />
        <ViewChip
          label="Upcoming"
          count={counts.upcoming}
          active={state.view === 'upcoming'}
          onClick={() => setState({ ...state, view: 'upcoming' })}
        />
        <ViewChip
          label="Past"
          count={counts.past}
          active={state.view === 'past'}
          onClick={() => setState({ ...state, view: 'past' })}
        />

        {tags.length > 0 ? <span className="h-5 w-px shrink-0 bg-line" aria-hidden /> : null}

        {tags.map((tag) => (
          <span key={tag} className="shrink-0">
            <Tag
              active={state.tag === tag}
              onClick={() => setState({ ...state, tag: state.tag === tag ? null : tag })}
            >
              {tag}
            </Tag>
          </span>
        ))}
      </div>
    </div>
  );
}

function ViewChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
        active
          ? 'bg-ink text-white'
          : 'bg-surface text-ink-muted border border-line hover:text-ink'
      }`}
    >
      <span>{label}</span>
      <span className={`tabular-nums text-xs ${active ? 'text-white/70' : 'text-ink-subtle'}`}>
        {count}
      </span>
    </button>
  );
}
